import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { IndianRupee, RefreshCw, CheckCircle2, XCircle, Clock, Undo2, Search, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";

const STATUS_STYLES = {
  paid: "bg-[#E9F9EF] text-[#1FA463]",
  created: "bg-[#FEF6E7] text-[#B7791F]",
  failed: "bg-[#FDECEC] text-[#D64545]",
  refunded: "bg-[#EEF2FF] text-[#4F46E5]",
};

export default function CashfreePaymentsTab({ kind = "all" }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [refundTarget, setRefundTarget] = useState(null);
  const [refundReason, setRefundReason] = useState("");
  const [refundAmount, setRefundAmount] = useState("");
  const [refunding, setRefunding] = useState(false);

  const load = () => {
    setLoading(true);
    const params = new URLSearchParams({ status: statusFilter, kind, q: query.trim() });
    api.get(`/admin/payments?${params.toString()}`)
      .then(({ data }) => setRows(data))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    const t = setTimeout(load, query ? 350 : 0);
    return () => clearTimeout(t);
  }, [statusFilter, kind, query]);

  const openRefund = (row) => {
    setRefundTarget(row);
    setRefundReason("");
    setRefundAmount(String(row.amount ?? ""));
  };

  const doRefund = async () => {
    if (!refundTarget) return;
    const amt = parseFloat(refundAmount);
    const max = Number(refundTarget.amount || 0);
    if (!amt || amt <= 0 || amt > max) {
      toast.error(`Enter an amount between ₹1 and ₹${max}.`);
      return;
    }
    setRefunding(true);
    try {
      const { data } = await api.post(`/admin/payments/${refundTarget.order_id}/refund`, {
        amount: amt,
        reason: refundReason.trim() || undefined,
      });
      toast.success(`${data.partial ? "Partial refund" : "Refund"} ${String(data.refund_status).toLowerCase()} — ₹${data.amount} to ${refundTarget.user_name || "customer"}. A confirmation email was sent.`);
      setRefundTarget(null);
      setRefundReason("");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setRefunding(false);
    }
  };

  return (
    <div data-testid="cashfree-payments-tab">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="font-display text-xl brand-forest flex items-center gap-2">
            <IndianRupee size={18} /> Cashfree payments
          </h2>
          <p className="text-sm text-gray-500">Automatic gateway transactions. Boost & contact packs unlock on confirmed payment — no manual approval needed.</p>
        </div>
        <button onClick={load} data-testid="refresh-payments" className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-[#7A1E2A] border border-brand-subtle rounded-full px-3 py-1.5">
          <RefreshCw size={13} /> Refresh
        </button>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-sm">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input
            data-testid="payment-search"
            placeholder="Search by email, name or order ID…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="pl-9 pr-8 h-10 rounded-full"
          />
          {query && (
            <button
              data-testid="clear-search"
              onClick={() => setQuery("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
            >
              <X size={15} />
            </button>
          )}
        </div>
        <div className="flex gap-2 flex-wrap">
          {["all", "paid", "refunded", "created", "failed"].map((s) => (
            <button
              key={s}
              data-testid={`filter-${s}`}
              onClick={() => setStatusFilter(s)}
              className={`text-xs font-semibold rounded-full px-3 py-1.5 capitalize ${statusFilter === s ? "bg-brand-forest text-white" : "bg-white border border-brand-subtle text-gray-600"}`}
            >
              {s === "created" ? "pending" : s}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-gray-500 py-10 text-center">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-gray-500 py-10 text-center" data-testid="no-payments">{query ? "No transactions match your search." : "No transactions yet."}</p>
      ) : (
        <div className="overflow-x-auto border border-brand-subtle rounded-xl">
          <table className="w-full text-sm">
            <thead className="bg-[#FBF6EC] text-left text-gray-500 text-xs uppercase tracking-wider">
              <tr>
                <th className="px-4 py-3">User</th>
                <th className="px-4 py-3">Package</th>
                <th className="px-4 py-3">Amount</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Order</th>
                <th className="px-4 py-3">Date</th>
                <th className="px-4 py-3 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const refunded = !!r.refund_status;
                const st = refunded ? "refunded" : (r.fulfilled ? "paid" : r.status);
                const Icon = st === "paid" ? CheckCircle2 : st === "failed" ? XCircle : st === "refunded" ? Undo2 : Clock;
                const canRefund = !refunded && (r.fulfilled || r.status === "paid");
                return (
                  <tr key={r.id} className="border-t border-brand-subtle" data-testid={`payment-row-${r.order_id}`}>
                    <td className="px-4 py-3">
                      <p className="font-medium text-gray-900">{r.user_name || "—"}</p>
                      <p className="text-xs text-gray-500">{r.user_email}</p>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-gray-900">{r.package_name || r.package_id}</p>
                      {r.property_title && <p className="text-xs text-gray-500 truncate max-w-[180px]">{r.property_title}</p>}
                    </td>
                    <td className="px-4 py-3 font-semibold">
                      ₹{Number(r.amount || 0).toLocaleString("en-IN")}
                      {refunded && <span className="block text-[11px] font-normal text-[#4F46E5]">−₹{Number(r.refund_amount || 0).toLocaleString("en-IN")} refunded</span>}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 text-xs font-semibold rounded-full px-2.5 py-1 ${STATUS_STYLES[st] || "bg-gray-100 text-gray-600"}`}>
                        <Icon size={12} /> {st === "created" ? "pending" : st}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500 font-mono">{r.order_id?.slice(0, 14)}…</td>
                    <td className="px-4 py-3 text-xs text-gray-500">{r.created_at ? new Date(r.created_at).toLocaleString("en-IN") : "—"}</td>
                    <td className="px-4 py-3 text-right">
                      {canRefund ? (
                        <Button
                          size="sm"
                          variant="outline"
                          data-testid={`refund-${r.order_id}`}
                          onClick={() => openRefund(r)}
                          className="h-8 rounded-full text-xs border-[#D64545] text-[#D64545] hover:bg-[#FDECEC]"
                        >
                          <Undo2 size={12} className="mr-1" /> Refund
                        </Button>
                      ) : refunded ? (
                        <span className="text-xs text-[#4F46E5] capitalize">{String(r.refund_status).toLowerCase()}</span>
                      ) : (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!refundTarget} onOpenChange={(o) => { if (!o) setRefundTarget(null); }}>
        <DialogContent className="rounded-2xl" data-testid="refund-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl brand-forest">Refund this payment?</DialogTitle>
            <DialogDescription>
              Refunding <strong>{refundTarget?.user_name || refundTarget?.user_email}</strong> for “{refundTarget?.package_name}”
              {" "}(paid ₹{Number(refundTarget?.amount || 0).toLocaleString("en-IN")}). A confirmation email is sent to the customer. This can't be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-sm font-semibold mb-1 block">Refund amount (₹)</label>
              <Input
                data-testid="refund-amount"
                type="number"
                min="1"
                max={refundTarget?.amount}
                value={refundAmount}
                onChange={(e) => setRefundAmount(e.target.value)}
              />
              <p className="text-xs text-gray-500 mt-1">
                Full amount is ₹{Number(refundTarget?.amount || 0).toLocaleString("en-IN")}. Enter a smaller value for a partial refund.
                {refundTarget?.package_kind === "feature" && Number(refundAmount) >= Number(refundTarget?.amount || 0) && " Full refund removes the boost from the listing."}
              </p>
            </div>
            <div>
              <label className="text-sm font-semibold mb-1 block">Reason <span className="text-gray-400 font-normal">(optional, shown internally)</span></label>
              <Input
                data-testid="refund-reason"
                placeholder="e.g. Duplicate charge / customer dispute"
                value={refundReason}
                onChange={(e) => setRefundReason(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" className="rounded-full" onClick={() => setRefundTarget(null)}>Cancel</Button>
            <Button
              data-testid="confirm-refund"
              onClick={doRefund}
              disabled={refunding}
              className="bg-[#D64545] hover:bg-[#B93B3B] text-white rounded-full"
            >
              {refunding ? "Processing…" : "Issue refund"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
